import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    ExperimentStatusEnum,
    GetProviderExperimentListOrderEnum,
    GetProviderExperimentListSortEnum,
    getOptions,
    IFilterSchema,
    ISelectOption,
    ITableColumn,
    ITableFetchOptions,
    ProviderExperiment,
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Row } from 'react-table';
import { DialogContext } from 'services';
import { IInjectedRootStore, IItemSortOrder, parseSortBy } from 'stores';
import { ClaimService } from 'stores/claim.service';
import * as S from './experiments-list.styles';

const formatProviderName = (provider?: ProviderExperiment['billingProvider']): string => {
    if (!provider) {
        return '';
    }

    if (provider.organization) {
        return provider.organization;
    }

    const parts = [provider.first, provider.last].filter(Boolean);
    return parts.join(' ');
};

const formatRenderingProvider = (provider?: ProviderExperiment['renderingProvider']): string => {
    if (!provider) {
        return '';
    }

    const parts = [provider.first, provider.last].filter(Boolean);
    if (parts.length > 0) {
        return parts.join(' ');
    }

    return provider.organization || '';
};

const experimentsColumns: ITableColumn<ProviderExperiment, GetProviderExperimentListSortEnum>[] = [
    {
        id: 'Id',
        Header: 'ID',
        accessor: (row): string => row.id || '',
        disableSortBy: true,
        Cell: DataTableCell('String'),
    },
    {
        id: 'Status',
        Header: 'Status',
        accessor: (row): string => row.status || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderExperimentListSortEnum.STATUS,
    },
    {
        id: 'Active',
        Header: 'Active',
        accessor: (row): boolean | undefined => row.active,
        disableSortBy: true,
        Cell: DataTableCell('Boolean'),
    },
    {
        id: 'CancelRequested',
        Header: 'Cancel Requested',
        accessor: (row): boolean | undefined => row.cancelRequested,
        disableSortBy: true,
        Cell: DataTableCell('Boolean'),
    },
    {
        id: 'BillingProvider',
        Header: 'Billing Provider',
        accessor: (row): string => formatProviderName(row.billingProvider),
        Cell: DataTableCell('String'),
        sortType: GetProviderExperimentListSortEnum.BILLINGPROVIDERID,
    },
    {
        id: 'PrimaryPayer',
        Header: 'Primary Payer',
        accessor: (row): string => row.primaryPayer?.name || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderExperimentListSortEnum.PRIMARYPAYERID,
    },
    {
        id: 'RenderingProvider',
        Header: 'Rendering Provider',
        accessor: (row): string => formatRenderingProvider(row.renderingProvider),
        Cell: DataTableCell('String'),
        sortType: GetProviderExperimentListSortEnum.RENDERINGPROVIDERID,
    },
    {
        id: 'Facility',
        Header: 'Facility',
        accessor: (row): string => row.facility?.name || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderExperimentListSortEnum.FACILITYID,
    },
    {
        id: 'FromDos',
        Header: 'From DoS',
        accessor: (row): string => row.fromDateOfService || '',
        Cell: DataTableCell('Date'),
        sortType: GetProviderExperimentListSortEnum.FROMDOS,
    },
    {
        id: 'ThroughDos',
        Header: 'Through DoS',
        accessor: (row): string => row.throughDateOfService || '',
        Cell: DataTableCell('Date'),
        sortType: GetProviderExperimentListSortEnum.THROUGHDOS,
    },
    {
        id: 'Created',
        Header: 'Created',
        accessor: (row): string => row.created || '',
        Cell: DataTableCell('DateTime'),
        sortType: GetProviderExperimentListSortEnum.CREATED,
    },
];

const experimentsFilterSchemas: IFilterSchema[] = [
    {
        label: 'ID',
        filterId: 'filterId',
        filterType: 'String',
        columnId: 'Id',
    },
    {
        label: 'Status',
        filterId: 'filterStatus',
        filterType: 'Select',
        columnId: 'Status',
        options: getOptions(ExperimentStatusEnum),
    },
    {
        label: 'Active',
        filterId: 'filterActive',
        filterType: 'Boolean',
        columnId: 'Active',
    },
    {
        label: 'Primary Payer',
        filterId: 'filterPrimaryPayerId',
        columnId: 'PrimaryPayer',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const payers = await ClaimService.queryPayers(query);
            return _.map(payers, p => ({
                label: p.name,
                value: p.id,
            }));
        },
    },
    {
        label: 'Billing Provider',
        filterId: 'filterBillingProviderId',
        columnId: 'BillingProvider',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const providers = await ClaimService.queryProviders(query);
            return _.map(providers, p => ({
                label: p.organization ? p.organization : `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Rendering Provider',
        filterId: 'filterRenderingProviderId',
        columnId: 'RenderingProvider',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const providers = await ClaimService.queryProviders(query);
            return _.map(providers, p => ({
                label: p.organization ? p.organization : `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Facility',
        filterId: 'filterFacilityId',
        columnId: 'Facility',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const facilities = await ClaimService.queryFacilities(query);
            return _.map(facilities, f => ({
                label: f.name,
                value: f.id,
            }));
        },
    },
    {
        label: 'From Date of Service',
        filterId: 'filterFromDos',
        filterType: 'Date',
        columnId: 'FromDos',
    },
    {
        label: 'Through Date of Service',
        filterId: 'filterThroughDos',
        filterType: 'Date',
        columnId: 'ThroughDos',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
];

type ExperimentsListProps = {};
type IInjectedProps = ExperimentsListProps & IInjectedRootStore;

export const ExperimentsList = inject('rootStore')(
    observer((props: ExperimentsListProps) => {
        const { rootStore } = props as IInjectedProps;
        if (!rootStore) {
            return <></>;
        }

        const presenter = useRef(
            new DataTablePresenter<ProviderExperiment>({
                name: 'provider-experiments',
                data: [],
                columns: experimentsColumns,
                filterSchemas: experimentsFilterSchemas,
            }),
        ).current;

        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            document.title = 'Verifications - Experiments';
        }, []);

        const onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            setLoading(true);

            try {
                authContext.cancelGroup?.('providerExperiments');
                authContext.setGroup?.('providerExperiments');

                const { sort, order } = parseSortBy(sortBy, experimentsColumns);
                const sortEnums = (sort as (GetProviderExperimentListSortEnum | undefined)[] | undefined)?.filter(
                    (value): value is GetProviderExperimentListSortEnum => Boolean(value),
                );
                const orderEnums = order?.map((value) =>
                    value === IItemSortOrder.ASCENDING
                        ? GetProviderExperimentListOrderEnum.ASCENDING
                        : GetProviderExperimentListOrderEnum.DESCENDING,
                );

                const listing = await rootStore.experimentsStore.loadExperimentsList({
                    page: pageIndex,
                    pageSize,
                    sort: sortEnums,
                    order: orderEnums,
                    filters: validFilters,
                });

                presenter.onDataUpdate(listing.experiments);
                presenter.onDataTotalChange(listing.total ?? listing.experiments.length);
            } catch (error) {
                dialogContext.showError?.(error);
            } finally {
                setLoading(false);
            }
        };

        const getRowHref = (row: Row<ProviderExperiment>): string => {
            return row.original.id ? `/verifications/experiments/${row.original.id}` : '#';
        };

        return (
            <PageLayout>
                <PageHeader title="Experiments" hideDateRange={true} />
                <PageLayoutRow>
                    <DataTableCard size="large">
                        <S.Wrapper>
                            <DataTable
                                presenter={presenter}
                                isLoading={loading}
                                onFetch={onFetch}
                                getRowHref={getRowHref}
                                hideSelectAll={true}
                                pageSizeOptions={[25, 50, 100]}
                            />
                        </S.Wrapper>
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);

